import { useCallback, useRef, useState } from 'react';

import { getMyWorkshopAnswers } from '../../../api/workshop/workshop.api';
import { saveIntakeAnswers } from '../utils/intakeAnswers';

/**
 * 예약 전 사전 질문 게이트.
 *
 * <p>질문이 있는 워크숍이면 모달을 띄우고, 답을 받은 뒤에야 원래 하려던 동작을 이어간다.
 * 질문이 없으면 모달 없이 바로 통과시킨다.
 *
 * @example
 * const gate = useIntakeGate();
 * await gate.run(workshop, () => navigate('/check-details'));
 * // 렌더 어딘가에 <IntakeQuestionsModal {...gate.modalProps} />
 */
const useIntakeGate = () => {
  const [state, setState] = useState({
    isOpen: false,
    questions: [],
    initialAnswers: {},
    workshopId: null,
  });
  const continuationRef = useRef(null);

  /**
   * @param {object} workshop  questions 를 담고 있는 워크숍 상세
   * @param {function} proceed 답변이 확보된 뒤 실행할 동작
   */
  const run = useCallback(async (workshop, proceed) => {
    const questions = workshop?.questions ?? [];
    if (questions.length === 0) {
      await proceed();
      return;
    }

    // 같은 워크숍을 다시 예약하는 경우 직전 답변을 채워준다
    const initialAnswers = await getMyWorkshopAnswers(workshop.id);
    continuationRef.current = proceed;
    setState({
      isOpen: true,
      questions,
      initialAnswers,
      workshopId: workshop.id,
    });
  }, []);

  /**
   * 장바구니처럼 여러 워크숍을 한 번에 예약하는 경우.
   * 질문이 있는 워크숍만 차례로 물어보고, 전부 끝나면 proceed 를 실행한다.
   */
  const runMany = useCallback(
    async (workshops, proceed) => {
      const pending = (workshops ?? []).filter(
        (workshop) => (workshop?.questions ?? []).length > 0,
      );
      if (pending.length === 0) {
        await proceed();
        return;
      }

      const next = async (index) => {
        if (index >= pending.length) {
          await proceed();
          return;
        }
        await run(pending[index], () => next(index + 1));
      };
      await next(0);
    },
    [run],
  );

  const close = useCallback(() => {
    continuationRef.current = null;
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const submit = useCallback(
    async (answers) => {
      // 보내는 시점은 화면 뒤쪽이라 저장해두고 createBookings 에서 꺼내 쓴다
      saveIntakeAnswers(state.workshopId, answers);
      const proceed = continuationRef.current;
      continuationRef.current = null;
      setState((prev) => ({ ...prev, isOpen: false }));
      if (!proceed) return;

      /*
       * 모달을 닫은 뒤에 원래 동작이 돌아가므로, 여기서 터지면 호출부의 try/catch 는
       * 이미 빠져나간 뒤다. 잡아서 알려주지 않으면 아무 일도 안 일어난 것처럼 보인다.
       */
      try {
        await proceed();
      } catch (error) {
        console.error('Failed after intake questions:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          'Something went wrong. Please try again.';
        alert(message);
      }
    },
    [state.workshopId],
  );

  return {
    run,
    runMany,
    modalProps: {
      /*
       * 여러 워크숍을 이어서 물을 때 isOpen 이 false→true 로 한 번에 batch 되면
       * 모달이 리마운트되지 않아 앞 워크숍의 답이 남는다. key 로 강제 교체한다.
       */
      key: state.workshopId ?? 'intake',
      isOpen: state.isOpen,
      questions: state.questions,
      initialAnswers: state.initialAnswers,
      onCancel: close,
      onSubmit: submit,
    },
  };
};

export default useIntakeGate;
